import styled from 'styled-components';
import { Dropdown, Form, InputGroup } from 'react-bootstrap';
import { COLORS } from 'modules/core/theme/colors';
import { STATIC_IMAGE_URLS } from 'modules/core/constants';
import { WindowBreakpoints } from 'modules/core/enums/windowBreakpoints.enum';

const { Toggle, Menu, Item } = Dropdown;

const OVERRIDER_DROPDOWN_SELECTORS = `
  &,
  &:hover,
  &:focus,
  &:active,
  &.btn-primary:not(:disabled):not(.disabled):active,
  .show > &.btn-primary.dropdown-toggle
`;

const DROPDOWN_ARROW_STYLES = `
  margin-left: 0.255em;
  vertical-align: 0.255em;
  content: '';
  position: absolute;
  right: 0.5em;
  top: 50% !important;
  transform: translateY(-50%);
  border-top: 0.3em solid;
  border-right: 0.3em solid transparent;
  border-bottom: 0;
  border-left: 0.3em solid transparent;
  color: black;
`;

export const CustomDropdownToggle = styled(Toggle)`
  width: 100%;
  height: 25px;
  justify-content: space-between;
  font-size: ${({ theme: { fontSize } }) => fontSize.normal};
  &:after {
    content: none;
  }
  background-image: url(${STATIC_IMAGE_URLS.icons.arrowIcon});
  background-repeat: no-repeat;
  background-position: right 0.4rem center;
  background-size: 11px 11px;
  padding-right: 22px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  padding-top: 0;
  padding-bottom: 0;
  transition: none;

  &:disabled {
    color: ${COLORS.GREY_24};
    background-color: ${COLORS.GREY_31};
    opacity: 1;
    border: 1px solid ${COLORS.GREY_32};
  }
`;

export const CustomDropdownDefaultToggle = styled(CustomDropdownToggle)<{ isInvalid: boolean }>`
  ${OVERRIDER_DROPDOWN_SELECTORS} {
    background-color: ${({ theme: { colors } }) => colors.white};
    border: 1px solid ${({ isInvalid }) => (isInvalid ? COLORS.RED_3 : COLORS.GREY_32)};
    color: ${({ theme: { colors } }) => colors.black};
    box-shadow: none;
  }

  &:disabled,
  &.disabled {
    color: ${COLORS.GREY_24} !important;
    background-color: ${COLORS.GREY_31} !important;
    border: 1px solid ${COLORS.GREY_32} !important;
    opacity: 1 !important;
    pointer-events: none;
  }
`;

export const CustomDropdownSelectedScoreToggle = styled(CustomDropdownToggle)`
  ${OVERRIDER_DROPDOWN_SELECTORS} {
    background-color: white;
    box-shadow: none;
    border: 1px solid ${COLORS.GREY_32};
    color: ${({ theme: { colors } }) => colors.black};
  }

  &:disabled,
  &.disabled {
    color: ${COLORS.GREY_24} !important;
    background-color: ${COLORS.GREY_31} !important;
    border: 1px solid ${COLORS.GREY_32} !important;
    opacity: 1 !important;
    pointer-events: none;
  }
`;

// Styles matching LoanVariablesDialog dropdown
export const TermInputGroupUI = styled(InputGroup)<{
  $isInvalid?: boolean;
  $isPlaceholder?: boolean;
  $showGreenBorder?: boolean;
}>`
  .btn.disabled,
  .btn:disabled,
  fieldset:disabled .btn {
    background-color: ${COLORS.GREY_31};
    opacity: 1;
    border: 1px solid ${COLORS.GREY_32} !important;
  }
  .btn {
    ${OVERRIDER_DROPDOWN_SELECTORS} {
      background-color: ${({ theme: { colors } }) => colors.white};
      border: 1px solid ${({ $isInvalid }) => ($isInvalid ? COLORS.RED_3 : COLORS.GREY_32)} !important;
      color: ${({ theme: { colors }, $isPlaceholder }) => ($isPlaceholder ? COLORS.GREY_24 : colors.black)};
      box-shadow: none;
      ${({ $isPlaceholder }) => !$isPlaceholder && `font-weight: bold;`};
      border-radius: 0.375rem !important;
    }
  }

  ${({ $showGreenBorder }) =>
    $showGreenBorder &&
    `
      .btn, .btn:hover, .btn:focus {
        background-color: ${COLORS.GREEN_10} !important;
        color: ${COLORS.GREEN_8} !important;
        border: 1px solid ${COLORS.GREEN_8} !important;
      }

      .btn::after {
        color: ${COLORS.GREEN_8} !important;
      }

      .btn:focus {
        box-shadow: 0px 0px 4px 1px ${COLORS.GREEN_8} !important;
      }
  `}
`;

export const TermDropdownContainer = styled(Dropdown)`
  .btn.disabled,
  .btn:disabled,
  fieldset:disabled .btn {
    color: ${COLORS.GREY_24};
    background-color: ${COLORS.GREY_31};
    opacity: 1;
    border: 1px solid ${COLORS.GREY_32};
  }
`;

export const TermDropdownToggle = styled(Toggle)`
  width: 100%;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: ${({ theme: { fontSize } }) => fontSize.normal};
  padding-top: 0;
  padding-bottom: 0;
  font-weight: 500 !important;
  transition: none;

  &::after {
    display: inline-block;
    ${DROPDOWN_ARROW_STYLES}
  }

  display: block;
  width: 100%;
  padding-left: 5px;
  padding-right: 5px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: left;

  &:disabled,
  &.disabled {
    color: ${COLORS.GREY_24} !important;
    background-color: ${COLORS.GREY_31} !important;
    border: 1px solid ${COLORS.GREY_32} !important;
    opacity: 1 !important;
  }
`;

export const TermDropdownMenu = styled(Menu)`
  width: 100%;
  padding: 0;
  max-height: 20vh;
  overflow: auto;
  min-width: 0;

  a:hover {
    background-color: ${COLORS.WHITE_4};
    color: ${COLORS.BLACK};
  }
`;

export const TermMenuItem = styled(Item)`
  font-size: ${({ theme: { fontSize } }) => fontSize.normal};
  text-align: center;
  padding: 5px;
  margin: 2px 0;
  position: relative;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CustomDropdownMenu = styled(Menu)`
  padding: 0;
  max-width: 300px;
  width: auto;
  position: absolute;
  max-height: 400px;
  overflow-y: scroll;

  @media (min-width: ${WindowBreakpoints.medium + 1}px) and (max-width: ${WindowBreakpoints.large}px) {
    max-width: 700px;
  }

  @media (min-width: ${WindowBreakpoints.small + 1}px) and (max-width: ${WindowBreakpoints.medium}px) {
    max-width: 500px;
  }
`;

export const OptionItem = styled(Item)<{ selected: boolean }>`
  font-size: ${({ theme: { fontSize } }) => fontSize.sm};
  padding: 0.375rem 0.75rem;
  position: relative;
  cursor: pointer;
  text-wrap: wrap;

  background-color: ${({ selected }) => selected && COLORS.GREY_27};

  &:active {
    background-color: ${COLORS.GREY_27};
    color: ${({ theme: { colors } }) => colors.black};
  }
`;

export const FormControl = styled(Form.Control)`
  &,
  &:hover,
  &:focus,
  &:active {
    box-shadow: unset;
    border: unset;
    border-bottom: 1px solid ${COLORS.GREY_22};
    border-radius: unset;
    background-color: ${COLORS.GREY_27};
  }
`;
